import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function MathText({ text }) {
  const ref = useRef();
  useEffect(() => {
    if (!ref.current || !text) return;
    const parts = text.split(/(\$[^$]+\$)/g);
    ref.current.innerHTML = parts.map(p => {
      if (p.startsWith('$') && p.endsWith('$')) {
        try {
          if (window.katex) return window.katex.renderToString(p.slice(1,-1), { throwOnError: false });
        } catch {}
      }
      return p.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    }).join('');
  }, [text]);
  return <span ref={ref} />;
}

export default function StudentResult() {
  const { attemptId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`/api/student/attempt/${attemptId}/result`)
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setData(d); })
      .catch(() => setError('Failed to load result'));
  }, [attemptId]);

  return <ResultView data={data} error={error} />;
}

// Reusable result display — used both by the standalone /result/:attemptId route above
// and by the "check your result" lookup on the public exam link (student/Exam.js), so the
// two entry points render an identical result view instead of duplicating this markup.
export function ResultView({ data, error }) {
  const downloadPDF = () => {
    const doc = new jsPDF();
    const { attempt, exam } = data;
    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text('Exam Result', 105, 15, { align: 'center' });
    doc.setFontSize(13); doc.setFont('helvetica', 'normal');
    doc.text(exam.title, 105, 24, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`Student: ${attempt.student_name}`, 20, 36);
    doc.text(`Phone: ${attempt.student_phone}`, 20, 43);
    if (attempt.roll_number) doc.text(`Roll No: ${attempt.roll_number}`, 20, 50);
    doc.text(`Score: ${attempt.score}/${attempt.total_marks}`, 120, 36);
    doc.text(`Percentage: ${attempt.percentage}%`, 120, 43);
    doc.text(`Rank: #${attempt.rank || '-'}`, 120, 50);

    if (data.question_details) {
      autoTable(doc, {
        startY: 60,
        head: [['Q#', 'Question', 'Your Answer', 'Correct Answer', 'Result']],
        body: data.question_details.map((q, i) => [
          i + 1,
          q.question_text.slice(0, 60),
          q.student_answer.join(', ') || '-',
          Array.isArray(q.correct_answers) ? q.correct_answers.join(', ') : '-',
          Array.isArray(q.correct_answers) ? (JSON.stringify([...q.student_answer].sort()) === JSON.stringify([...q.correct_answers].sort()) ? '✓' : '✗') : '-'
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [99, 102, 241] },
        columnStyles: { 4: { halign: 'center' } }
      });
    }
    doc.save(`result_${attempt.student_name}.pdf`);
  };

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#0f0f1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Sora, sans-serif' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap');`}</style>
      <div style={{ textAlign: 'center', color: '#94a3b8', padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
        <h2 style={{ color: '#e2e8f0', marginBottom: 8 }}>Result Not Available</h2>
        <p>{error}</p>
      </div>
    </div>
  );

  if (!data) return (
    <div style={{ minHeight: '100vh', background: '#0f0f1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 40, height: 40, border: '3px solid #2e2e50', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const { attempt, exam, question_details } = data;
  const pct = attempt.percentage;
  const grade = pct >= 90 ? { label: 'A+', color: '#10b981' } : pct >= 80 ? { label: 'A', color: '#34d399' } : pct >= 70 ? { label: 'B', color: '#6366f1' } : pct >= 60 ? { label: 'C', color: '#f59e0b' } : pct >= 40 ? { label: 'D', color: '#f97316' } : { label: 'F', color: '#ef4444' };

  return (
    <div style={{ minHeight: '100vh', background: '#0f0f1a', fontFamily: 'Sora, sans-serif', color: '#e2e8f0' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap');
        *{margin:0;padding:0;box-sizing:border-box;}
        body{background:#0f0f1a;}
        @keyframes fadeIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1e1e35 0%, #16213e 100%)', borderBottom: '1px solid #2e2e50', padding: '28px 20px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>📊 Exam Result</h1>
        <p style={{ color: '#94a3b8', fontSize: 14 }}>{exam.title} • {exam.subject}</p>
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '32px 20px' }}>

        {/* Score Card */}
        <div style={{ background: '#1e1e35', border: '1px solid #2e2e50', borderRadius: 20, padding: 32, marginBottom: 24, textAlign: 'center', animation: 'fadeIn 0.4s ease' }}>
          <div style={{ width: 100, height: 100, borderRadius: '50%', background: `${grade.color}22`, border: `4px solid ${grade.color}`, margin: '0 auto 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontWeight: 800, fontSize: 28, color: grade.color }}>{grade.label}</div>
          </div>
          <div style={{ fontSize: 48, fontWeight: 800, color: '#818cf8', marginBottom: 4 }}>{pct}%</div>
          <div style={{ fontSize: 18, color: '#94a3b8', marginBottom: 20 }}>{attempt.score} / {attempt.total_marks} marks</div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {[
              { label: 'Correct', value: attempt.correct_count, color: '#10b981' },
              { label: 'Wrong', value: attempt.incorrect_count, color: '#ef4444' },
              { label: 'Time Used', value: `${Math.floor((attempt.time_used || 0) / 60)}m`, color: '#6366f1' },
            ].map(s => (
              <div key={s.label} style={{ background: '#252542', borderRadius: 12, padding: '14px 8px' }}>
                <div style={{ fontWeight: 800, fontSize: 22, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Student Info */}
        <div style={{ background: '#1e1e35', border: '1px solid #2e2e50', borderRadius: 16, padding: 20, marginBottom: 24, animation: 'fadeIn 0.4s ease 0.1s both' }}>
          <h3 style={{ fontWeight: 700, marginBottom: 14, fontSize: 15 }}>👤 Student Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { label: 'Name', value: attempt.student_name },
              { label: 'Phone', value: attempt.student_phone },
              attempt.roll_number && { label: 'Roll No', value: attempt.roll_number },
              { label: 'Submitted', value: attempt.submitted_at ? new Date(attempt.submitted_at).toLocaleString() : '-' },
            ].filter(Boolean).map(item => (
              <div key={item.label}>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{item.label}</div>
                <div style={{ fontSize: 14, color: '#e2e8f0' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Answer Review */}
        {question_details && question_details.length > 0 && (
          <div style={{ animation: 'fadeIn 0.4s ease 0.2s both' }}>
            <h3 style={{ fontWeight: 700, marginBottom: 16, fontSize: 15 }}>📝 Answer Review</h3>
            {question_details.map((q, i) => {
              const hasAnswerKey = Array.isArray(q.correct_answers);
              const isCorrect = hasAnswerKey && JSON.stringify([...q.student_answer].sort()) === JSON.stringify([...q.correct_answers].sort());
              const notAttempted = q.student_answer.length === 0;
              return (
                <div key={q.id} style={{
                  background: '#1e1e35',
                  border: `1px solid ${!hasAnswerKey ? '#2e2e50' : isCorrect ? '#10b98133' : notAttempted ? '#2e2e50' : '#ef444433'}`,
                  borderRadius: 14, padding: 18, marginBottom: 14
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
                      <span style={{ color: '#6366f1', marginRight: 8 }}>Q{i + 1}.</span><MathText text={q.question_text} />
                    </div>
                    {hasAnswerKey && (
                      <span style={{
                        flexShrink: 0, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                        background: isCorrect ? 'rgba(16,185,129,0.2)' : notAttempted ? 'rgba(100,116,139,0.2)' : 'rgba(239,68,68,0.2)',
                        color: isCorrect ? '#34d399' : notAttempted ? '#94a3b8' : '#f87171'
                      }}>{isCorrect ? '✓ Correct' : notAttempted ? '— Skipped' : '✗ Wrong'}</span>
                    )}
                  </div>
                  {q.question_image && <img src={q.question_image} alt="question" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 10 }} />}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {q.options.map((opt, oi) => {
                      const isCorrectOpt = hasAnswerKey && q.correct_answers.includes(opt);
                      const isStudentOpt = q.student_answer.includes(opt);
                      let bg = '#252542', border = '#2e2e50', color = '#94a3b8';
                      if (isCorrectOpt) { bg = 'rgba(16,185,129,0.15)'; border = '#10b981'; color = '#34d399'; }
                      else if (isStudentOpt) { bg = 'rgba(239,68,68,0.15)'; border = '#ef4444'; color = '#f87171'; }
                      return (
                        <div key={oi} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: '8px 12px', fontSize: 13, color }}>
                          {isCorrectOpt && '✓ '}{isStudentOpt && !isCorrectOpt && '✗ '}<MathText text={opt} />
                        </div>
                      );
                    })}
                  </div>
                  {hasAnswerKey && !isCorrect && !notAttempted && (
                    <div style={{ marginTop: 10, fontSize: 12, color: '#34d399' }}>
                      ✓ Correct: {q.correct_answers.join(', ')}
                    </div>
                  )}
                  {q.explanation && (
                    <div style={{ marginTop: 12, padding: 12, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 10, fontSize: 13, color: '#c7d2fe' }}>
                      <div style={{ fontWeight: 700, marginBottom: 4, color: '#818cf8' }}>💡 Explanation</div>
                      <MathText text={q.explanation} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Download PDF */}
        {data.allow_pdf_download && (
          <button onClick={downloadPDF} style={{
            width: '100%', padding: '14px', background: '#6366f1', color: 'white',
            border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700,
            cursor: 'pointer', marginTop: 8, fontFamily: 'Sora, sans-serif'
          }}>📄 Download Result PDF</button>
        )}
      </div>
    </div>
  );
}
